import * as z from 'zod'

import {validateComparisonTable} from '@github-ui/swp-core/components/contentful/ContentfulFeatureComparisonTable'
import {ComparisonTableSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/comparisonTable'
import {
  PrimerComponentCardSchema,
  type PrimerComponentCard,
} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentCard'
import {PrimerComponentSectionIntroSchema} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentSectionIntro'
import {buildEntrySchemaFor} from '@github-ui/swp-core/schemas/contentful/entry'
import {safeOptional} from '@github-ui/swp-core/schemas/safe-optional'

import {defineBrandPage, optionalSection, optionalTemplateField} from '../../brand/components/BrandPage'
import {
  BrandContentError,
  findByContentType,
  findByIdWithSchema,
  GenericContentSchema,
  GenericSectionSchema,
  parseEntries,
  requireById,
  resolveGenericContentCta,
  TemplateBrandHeroSchema,
} from '../../brand/lib/types/contentful'

export const pricingComparisonView = 'github'

function toPricingCard(card: PrimerComponentCard): PrimerComponentCard {
  return {...card, fields: {...card.fields, headingLevel: 'h3'}}
}

const CustomerStorySchema = GenericContentSchema.transform(story => ({
  story,
  image: story.fields.media?.at(0),
  cta: resolveGenericContentCta(story),
}))

const ComparisonSectionSchema = buildEntrySchemaFor('genericSection', {
  fields: z.lazy(() =>
    z.object({
      id: z.literal('comparisonTable'),
      htmlId: z.string().optional(),
      sectionIntro: safeOptional(PrimerComponentSectionIntroSchema),
      content: z.tuple([ComparisonTableSchema]),
    }),
  ),
})

export const pricingPage = defineBrandPage({
  hero: optionalTemplateField(
    'hero',
    TemplateBrandHeroSchema.transform(content => {
      const section = findByContentType([content], 'genericSection')
      const hero =
        findByContentType([content], 'primerComponentHero') ??
        findByContentType(section?.fields.content, 'primerComponentHero')

      if (!hero) throw new BrandContentError('Pricing hero requires a primerComponentHero entry')

      return hero
    }),
  ),
  pricingOptions: optionalSection('pricingOptions', GenericSectionSchema, section => {
    const pricing = findByContentType(section.fields.content, 'primerComponentPricingOptions')

    return pricing
      ? {
          htmlId: section.fields.htmlId,
          pricing: {
            ...pricing,
            fields: {
              ...pricing.fields,
              items: pricing.fields.items.map(plan => ({
                ...plan,
                fields: {...plan.fields, accordionHeadingLevel: plan.fields.accordionHeadingLevel ?? 'h3'},
              })),
            },
          },
        }
      : undefined
  }),
  addOns: optionalSection('addOns', GenericSectionSchema, section => ({
    htmlId: section.fields.htmlId,
    intro: section.fields.sectionIntro,
    cards: (findByContentType(section.fields.content, 'primerCards')?.fields.cards ?? []).map(toPricingCard),
  })),
  customerEvidence: optionalSection('customerEvidence', GenericSectionSchema, section => ({
    htmlId: section.fields.htmlId,
    logos: findByContentType(section.fields.content, 'primerComponentLogoSuite'),
    customerStory: findByIdWithSchema(section.fields.content, 'customerStoryBanner', CustomerStorySchema),
  })),
  comparisonTable: optionalSection('comparisonTable', ComparisonSectionSchema, section => {
    const table = section.fields.content[0]
    validateComparisonTable(table, pricingComparisonView)

    return {
      htmlId: section.fields.htmlId,
      intro: section.fields.sectionIntro,
      table,
    }
  }),
  resources: optionalSection('resources', GenericSectionSchema, section => {
    const group = requireById(section.fields.content, 'audienceCards', GenericContentSchema)

    return {
      htmlId: section.fields.htmlId,
      intro: section.fields.sectionIntro,
      cards: parseEntries(group.fields.content, PrimerComponentCardSchema).map(toPricingCard),
    }
  }),
})

export type PricingCustomerStoryContent = z.infer<typeof CustomerStorySchema>
export type PricingCardsContent = NonNullable<ReturnType<typeof pricingPage.resources.parse>['value']>
